import {useMemo, useState} from 'react'
import {useApolloClient, useQuery} from '@apollo/client'
import {Alert, Badge, Button, Form, Modal, ProgressBar, Spinner, Table} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import * as XLSX from 'xlsx'
import {
  CREATE_POLICY,
  DELETE_POLICIES_BY_REFERENCES,
  UPDATE_POLICY_FULL,
} from '../../../gql/mutations/policyMutation'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {GET_ALL_CARRIERS_LIST} from '../../../gql/queries/carriersQuery'
import {GET_ALL_POLICY_TYPE_CATEGORIES} from '../../../gql/queries/policyTypesQuery'
import {GET_ALL_STATES_EU_LIST} from '../../../gql/queries/statesEUQuery'
import {GET_ALL_POLICIES_STATUSES} from '../../../gql/queries/policiesQueries'
import {parseId} from '../../../helpers'
import {getMetalLevelValueByLabel} from '../../../helpers/consts'

type Cell = string | number | boolean | Date | null | undefined
type CatalogItem = {id: string; name?: string; firstName?: string; lastName?: string; npn?: string}
type ImportFormat = 'client-report' | 'client-report-es' | 'legacy-export'

interface ImportRow {
  rowNumber: number
  reference: string
  agent: string
  holderName: string
  values: Cell[]
  format: ImportFormat
}

interface ParsedFile {
  rows: ImportRow[]
  errors: string[]
  fileName: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onImported: () => void
}

// i18n-ok-start: las tres tablas de abajo son las cabeceras del fichero que se
// importa —el contrato del Excel que exporta la agencia—, no interfaz. Cambiar
// una rompe la importación (plan §10.8).
const BASE_HEADERS = [
  'Reference',
  'Agency',
  'Agent',
  'Full name',
  'First name',
  'Middle name',
  'Last name',
  'Date of birth',
  'Gender',
  'Email',
  'Phone',
  'Legal Status',
  'Social Security Number',
  'Green card',
  'Work permit',
  'Estado civil',
  'Address # 1',
  'Address # 2',
  'City',
  'State / Province',
  'Zip code',
  'County',
  'Employer name',
  'Company Phone',
  'Position / Occupation',
  'Annual income',
  'Policy number',
  'Marketplace ID',
  'Contract identification',
  'Number of applicants',
  'Effective date',
  'Company',
  'Insurance plan',
  'Type of plan',
  'Tax Credit / Subsidy',
  'Monthly premium amount',
  'Status',
  'Tags',
  'Period',
  'Confirmed consent',
  'Registration date',
  'Update date',
  'Renewal status',
]
const MEMBER_HEADERS = [
  'Is this member an applicant?',
  'Dependency type',
  'First name',
  'Last name',
  'Date of birth',
  'Gender',
  'Email',
  'Phone',
  'Legal Status',
  'Social Security Number',
]

const BASE_HEADERS_ES = [
  'Referencia',
  'Agencia',
  'Agente',
  'Nombre y Apellido',
  'Nombre',
  'Segundo nombre',
  'Apellido',
  'Fecha de nacimiento',
  'Genero',
  'Correo electrónico',
  'Teléfono',
  'Estatus Legal',
  'Número de Seguridad Social',
  'Green card',
  'Permiso de trabajo',
  'Estado civil',
  'Dirección #1',
  'Dirección #2',
  'Ciudad',
  'Estado / Provincia',
  'Código postal',
  'Condado',
  'Nombre del empleador',
  'Teléfono del empleador',
  'Puesto / Ocupación',
  'Ingresos anuales',
  'Número de póliza',
  'Marketplace ID',
  'Identificación del contrato',
  'Número de aplicantes',
  'Fecha efectiva',
  'Compañía',
  'Plan del seguro',
  'Tipo de plan',
  'Crédito Fiscal / Subsidio',
  'Monto prima mensual',
  'Estatus',
  'Etiquetas',
  'Período',
  'Consentimiento confirmado',
  'Fecha de registro',
  'Fecha de actualización',
  'Estatus de renovación',
]
const MEMBER_HEADERS_ES = [
  '¿Es este miembro un aplicante?',
  'Tipo de dependencia',
  'Nombre',
  'Apellido',
  'Fecha de nacimiento',
  'Genero',
  'Correo electrónico',
  'Teléfono',
  'Estatus Legal',
  'Número de Seguridad Social',
]

const LEGACY_BASE_HEADERS = [
  'AGENT',
  'AGENT NPN',
  'BROKER/AGENCY',
  'IS APPLICANT',
  'FULL NAME',
  'FIRST NAME',
  'MIDDLE NAME',
  'LAST NAME',
  'SECOND LAST NAME',
  'GENDER',
  'DATE OF BIRTH',
  'PHONE',
  'EMAIL',
  'ANNUAL INCOME',
  'MARITAL STATUS',
  'SSN',
  'COUNTRY OF BIRTH',
  'IMMIGRATION STATUS',
  'IMMIGRATION CATEGORY',
  'ADDRESS 1',
  'ADDRESS 2',
  'CITY',
  'STATE',
  'ZIP',
  'COUNTY',
  'MAILING ADDRESS 1',
  'MAILING ADDRESS 2',
  'MAILING CITY',
  'MAILING STATE',
  'MAILING ZIP',
  'MAILING COUNTY',
  'CARRIER/ISSUER',
  'PLAN',
  'PLAN ID',
  'MARKETPLACE ID',
  'MEMBER ID',
  'APPLICANTS',
  'POLICY COST',
  'SUBSIDY',
  'MONTHLY PREMIUM',
  'EFFECTIVE DATE',
  'STATUS',
  'ASSIGNED TO',
  'SUBMITTED',
  'LAST UPDATE',
  'CANCELLATION DATE',
  'IS DUPLICATE',
  'LAST DUPLICATE',
  'NPN USED IN MARKETPLACE',
  'FFM USED IN MARKETPLACE',
  'INTERNAL CODE',
]
// i18n-ok-end
const LEGACY_MEMBER_HEADERS = [
  'IS APPLICANT',
  'RELATIONSHIP',
  'FIRST NAME',
  'MIDDLE NAME',
  'LAST NAME',
  'SECOND LAST NAME',
  'GENDER',
  'DATE OF BIRTH',
  'PHONE',
  'EMAIL',
  'ANNUAL INCOME',
  'MARITAL STATUS',
  'SSN',
  'COUNTRY OF BIRTH',
  'IMMIGRATION STATUS',
  'IMMIGRATION CATEGORY',
]

const isLegacyRow = (row: ImportRow) => row.format === 'legacy-export'
const rowCompany = (row: ImportRow) => row.values[31]
const rowState = (row: ImportRow) => row.values[isLegacyRow(row) ? 22 : 19]

const text = (value: Cell) => String(value ?? '').trim()
const normalize = (value: Cell) =>
  text(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
const number = (value: Cell) => Number(text(value).replace(/[^0-9.-]/g, '')) || 0

const inferMetalLevel = (planName: Cell, explicitMetal?: Cell) => {
  const tokens = normalize(`${text(explicitMetal)} ${text(planName)}`).split(' ')
  // i18n-ok: etiquetas de nivel metalico que alimentan `getMetalLevelValueByLabel`
  const aliases: Array<[string, string[]]> = [
    ['Platinum', ['platinum', 'platino']],
    ['Gold', ['gold', 'oro']],
    ['Silver', ['silver', 'plata']],
    ['Bronze', ['bronze', 'bronce']],
    ['Catastrophic', ['catastrophic', 'catastrofico']],
  ]
  const metal = aliases.find(([, values]) => values.some((value) => tokens.includes(value)))
  // i18n-ok: valor de datos, no texto
  const label = metal?.[0] || (tokens.includes('csr') ? 'Silver' : '')
  return label ? Number(getMetalLevelValueByLabel(label)) : 0
}

const COMPANY_STOP_WORDS = new Set([
  'and',
  'the',
  'of',
  'de',
  'del',
  'la',
  'insurance',
  'health',
  'care',
  'healthcare',
  'company',
  'inc',
  'llc',
  'corp',
  'corporation',
])

const companyTokens = (value: Cell) =>
  normalize(value)
    .replace(/\bhealthcare\b/g, 'health care')
    .split(' ')
    .filter((token) => token && !COMPANY_STOP_WORDS.has(token))

const findCarrier = (company: Cell, carriers: CatalogItem[]) => {
  const normalizedCompany = normalize(company)
  const exactMatches = carriers.filter((carrier) => normalize(carrier.name) === normalizedCompany)
  if (exactMatches.length === 1) return exactMatches[0]
  if (exactMatches.length > 1) return null

  const wanted = Array.from(new Set(companyTokens(company)))
  if (!wanted.length) return null

  const ranked = carriers
    .map((carrier) => {
      const available = Array.from(new Set(companyTokens(carrier.name)))
      const common = wanted.filter((token) => available.includes(token)).length
      return {
        carrier,
        available,
        common,
        score: common / Math.max(wanted.length, available.length),
      }
    })
    .filter(({common}) => common > 0)
    .sort((a, b) => b.score - a.score || b.common - a.common)

  const best = ranked[0]
  if (!best) return null

  // A short commercial name such as "United" is safe only when it identifies one carrier.
  if (wanted.length === 1) {
    const candidates = ranked.filter(({available}) => available.includes(wanted[0]))
    return candidates.length === 1 ? candidates[0].carrier : null
  }

  const second = ranked[1]
  const clearlyBetter = !second || best.score - second.score >= 0.15
  return best.score >= 0.7 && clearlyBetter ? best.carrier : null
}

const toIsoDate = (value: Cell) => {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return value.toISOString().slice(0, 10)
  if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value)
    return date
      ? `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`
      : null
  }
  const match = text(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (match) return `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`
  const parsed = new Date(text(value))
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10)
}

const mapApplicant = (value: Cell) => ['yes', 'si', 'true', '1'].includes(normalize(value))
const mapGender = (value: Cell) =>
  ({m: 'Male', male: 'Male', masculino: 'Male', f: 'Female', female: 'Female', femenino: 'Female'}[
    normalize(value)
  ] ||
  text(value) ||
  null)
// i18n-ok-start: `mapImmigration` y `mapRelationship` normalizan la celda al
// valor que espera la API. Lo que devuelven viaja en el payload, no se pinta.
const mapImmigration = (value: Cell) => {
  const status = normalize(value)
  if (
    status === 'citizen' ||
    status === 'citizen naturalized' ||
    status === 'ciudadano' ||
    status === 'ciudadana'
  )
    return 'CITIZEN'
  if (
    status === 'resident' ||
    status.includes('permanent resident') ||
    status.includes('residente')
  )
    return 'RESIDENT'
  if (status.includes('asylum') || status.includes('asilo')) return 'ASYLUM'
  if (
    status.includes('work permit') ||
    status.includes('work authorization') ||
    status.includes('permiso de trabajo') ||
    status === 'ead'
  )
    return 'EAD'
  if (status.includes('temporary protected') || status === 'tps') return 'TPS'
  if (status.includes('humanitarian parole')) return 'HUMANITARIAN_PAROLE'
  return status ? 'OTHER' : null
}
const mapRelationship = (value: Cell) => {
  const relationship = normalize(value)
  if (relationship === 'spouse' || relationship === 'conyuge') return 'Spouse'
  if (
    ['child', 'dependent', 'son', 'daughter', 'hijo', 'hija', 'dependiente'].includes(relationship)
  )
    return 'Child'
  if (['parent', 'mother', 'father', 'mother or father', 'padre', 'madre'].includes(relationship))
    return 'Mother or Father'
  if (
    relationship.includes('brother') ||
    relationship.includes('sister') ||
    relationship.includes('hermano') ||
    relationship.includes('hermana')
  )
    return 'Brother or Sister'
  if (
    relationship.includes('grandson') ||
    relationship.includes('granddaughter') ||
    relationship.includes('nieto') ||
    relationship.includes('nieta')
  )
    return 'Grandson or Granddaughter'
  return relationship ? 'Other Relationship' : null
}

// i18n-ok-end
const mapPolicyStatus = (
  value: Cell,
  statuses: Array<{code: string; title?: string; category?: string}>
) => {
  const raw = normalize(value)
  if (!raw) return null
  const exact = statuses.find(
    (status) => normalize(status.code) === raw || normalize(status.title) === raw
  )
  if (exact) return exact.code

  const categoryAliases: Record<string, string> = {
    procesada: 'COMPLETED',
    procesado: 'COMPLETED',
    processed: 'COMPLETED',
    actualizada: 'COMPLETED',
    actualizado: 'COMPLETED',
    updated: 'COMPLETED',
    completed: 'COMPLETED',
    borrador: 'PENDING',
    draft: 'PENDING',
    pendiente: 'PENDING',
    pending: 'PENDING',
    cancelada: 'CANCELED',
    cancelado: 'CANCELED',
    canceled: 'CANCELED',
    cancelled: 'CANCELED',
  }
  const category = categoryAliases[raw]
  if (!category) return null
  const matches = statuses.filter((status) => normalize(status.category) === normalize(category))
  return (
    matches.find((status) => normalize(status.code) === normalize(category))?.code ||
    (matches.length === 1 ? matches[0].code : null)
  )
}

const nameMatches = (shortName: string, catalogName: string) => {
  const wanted = normalize(shortName).split(' ').filter(Boolean)
  const available = normalize(catalogName).split(' ').filter(Boolean)
  let position = 0
  const orderedMatch = wanted.every((part) => {
    const found = available.indexOf(part, position)
    if (found < 0) return false
    position = found + 1
    return true
  })
  if (orderedMatch) return true

  // Middle names are commonly present in one system but omitted in the other.
  return (
    wanted.length >= 2 &&
    available.length >= 2 &&
    wanted[0] === available[0] &&
    wanted[wanted.length - 1] === available[available.length - 1]
  )
}

const findAgentOfRecord = (value: Cell, users: CatalogItem[]) => {
  const rawValue = text(value)
  if (!rawValue) return null

  const digits = rawValue.replace(/\D/g, '')
  const numericValue = /^[\d\s().+-]+$/.test(rawValue) && !!digits
  const matches = numericValue
    ? users.filter((user) => text(user.npn).replace(/\D/g, '') === digits)
    : users.filter((user) => nameMatches(rawValue, `${user.firstName} ${user.lastName}`))

  return matches.length === 1 ? matches[0] : null
}

const M = defineMessages({
  title: {id: 'policies.list.import.title', defaultMessage: 'Import policies'},
  intro: {
    id: 'policies.list.import.intro',
    defaultMessage:
      'Select an Excel file exported with the recognized PolicyTools client format. Agency is ignored; policies are imported into your current organization.',
  },
  fileLabel: {id: 'policies.list.import.fileLabel', defaultMessage: 'Excel file (.xlsx or .xls)'},
  readingFile: {id: 'policies.list.import.readingFile', defaultMessage: 'Reading file…'},
  policiesCount: {
    id: 'policies.list.import.policiesCount',
    defaultMessage: '{count, plural, one {# policy} other {# policies}}',
  },
  cannotImport: {
    id: 'policies.list.import.cannotImport',
    defaultMessage: 'The file cannot be imported.',
  },
  validating: {
    id: 'policies.list.import.validating',
    defaultMessage: 'Validating agents and companies…',
  },
  needCorrection: {
    id: 'policies.list.import.needCorrection',
    defaultMessage:
      '{count, plural, one {# row needs correction before importing.} other {# rows need correction before importing.}}',
  },
  colRow: {id: 'policies.list.import.col.row', defaultMessage: 'Row'},
  colAgent: {id: 'policies.list.import.col.agent', defaultMessage: 'Agent'},
  colCompany: {id: 'policies.list.import.col.company', defaultMessage: 'Company'},
  colState: {id: 'policies.list.import.col.state', defaultMessage: 'State'},
  colProblem: {id: 'policies.list.import.col.problem', defaultMessage: 'Problem'},
  agentNotFound: {
    id: 'policies.list.import.problem.agentNotFound',
    defaultMessage: 'Agent not found or ambiguous',
  },
  companyNotFound: {
    id: 'policies.list.import.problem.companyNotFound',
    defaultMessage: 'Company not found or ambiguous',
  },
  stateNotFound: {
    id: 'policies.list.import.problem.stateNotFound',
    defaultMessage: 'State not found',
  },
  statusNotFound: {
    id: 'policies.list.import.problem.statusNotFound',
    defaultMessage: 'Status not found or ambiguous',
  },
  ready: {
    id: 'policies.list.import.ready',
    defaultMessage: 'Format, agents and companies recognized. The file is ready to import.',
  },
  acaTypeMissing: {
    id: 'policies.list.import.acaTypeMissing',
    defaultMessage: 'The ACA policy type is not configured. Configure it before importing.',
  },
  replaceLabel: {
    id: 'policies.list.import.replaceLabel',
    defaultMessage: 'Replace existing policies with the same Reference',
  },
  replaceWarning: {
    id: 'policies.list.import.replaceWarning',
    defaultMessage:
      'Existing policies matching these References and their related members will be deleted before importing the new rows.',
  },
  createdCount: {
    id: 'policies.list.import.createdCount',
    defaultMessage: '{count, plural, one {# policy created.} other {# policies created.}}',
  },
  replacedCount: {
    id: 'policies.list.import.replacedCount',
    defaultMessage:
      '{count, plural, one {# existing policy replaced.} other {# existing policies replaced.}}',
  },
  failedCount: {
    id: 'policies.list.import.failedCount',
    defaultMessage: '{count, plural, one {# row failed:} other {# rows failed:}}',
  },
  importing: {id: 'policies.list.import.importing', defaultMessage: 'Importing…'},
  btnReplaceImport: {
    id: 'policies.list.import.btn.replaceImport',
    defaultMessage: 'Replace & import',
  },
  btnImport: {id: 'policies.list.import.btn.import', defaultMessage: 'Import'},

  /**
   * Validacion del fichero. Se acumulan en `errors[]` y se pintan en el modal,
   * asi que **si** llegan al usuario; estaban a fuego en espanol.
   * Ojo: las cabeceras del CSV que se comparan (`BASE_HEADERS`) NO se traducen
   * — son el contrato del fichero que exporta la agencia (§10.8).
   */
  errColumnMismatch: {
    id: 'policies.list.import.error.columnMismatch',
    defaultMessage: 'Column {column}: expected “{expected}” but found “{found}”.',
  },
  errColumnEmpty: {id: 'policies.list.import.error.columnEmpty', defaultMessage: '(empty)'},
  errMemberHeader: {
    id: 'policies.list.import.error.memberHeader',
    defaultMessage: 'Column {column}: unrecognized member header (“{found}”).',
  },
  errMemberBlocks: {
    id: 'policies.list.import.error.memberBlocks',
    defaultMessage: 'Member blocks are incomplete.',
  },
  errRowNoReference: {
    id: 'policies.list.import.error.rowNoReference',
    defaultMessage: 'Row {row}: Reference is missing.',
  },
  errRowNoAgent: {
    id: 'policies.list.import.error.rowNoAgent',
    defaultMessage: 'Row {row}: Agent is missing.',
  },
  errRowNoHolder: {
    id: 'policies.list.import.error.rowNoHolder',
    defaultMessage: 'Row {row}: the holder name is missing.',
  },
  errRowDuplicateReference: {
    id: 'policies.list.import.error.rowDuplicateReference',
    defaultMessage: 'Row {row}: Reference is duplicated (it also appears on row {previousRow}).',
  },
  errImportNotStarted: {
    id: 'policies.list.import.error.notStarted',
    defaultMessage: 'The import did not start: {message}',
  },
})

export const ModalImportPolicies = ({isOpen, onClose, onImported}: Props) => {
  const intl = useIntl()
  const client = useApolloClient()
  const [parsed, setParsed] = useState<ParsedFile | null>(null)
  const [reading, setReading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [processed, setProcessed] = useState(0)
  const [result, setResult] = useState<{created: number; deleted: number; errors: string[]} | null>(
    null
  )

  const {data: usersData, loading: loadingUsers} = useQuery(GET_ALL_USERS_LIST, {
    fetchPolicy: 'network-only',
    skip: !isOpen,
  })
  const {data: carriersData, loading: loadingCarriers} = useQuery(GET_ALL_CARRIERS_LIST, {
    skip: !isOpen,
  })
  const {data: policyTypesData, loading: loadingTypes} = useQuery(GET_ALL_POLICY_TYPE_CATEGORIES, {
    skip: !isOpen,
  })
  const {data: statesData, loading: loadingStates} = useQuery(GET_ALL_STATES_EU_LIST, {
    skip: !isOpen,
  })
  const {data: statusesData, loading: loadingStatuses} = useQuery(GET_ALL_POLICIES_STATUSES, {
    variables: {first: 100},
    fetchPolicy: 'network-only',
    skip: !isOpen,
  })

  const users: CatalogItem[] = useMemo(() => usersData?.allUsersList || [], [usersData])
  const carriers: CatalogItem[] = useMemo(
    () => carriersData?.allInsuranceOperatorsList || [],
    [carriersData]
  )
  const states: Array<CatalogItem & {code?: string}> = useMemo(
    () => statesData?.allStatesList || [],
    [statesData]
  )
  const statuses: Array<{code: string; title?: string; category?: string}> = useMemo(
    () => statusesData?.allPolicyStatuses?.edges?.map(({node}: any) => node) || [],
    [statusesData]
  )
  const acaPolicyType = policyTypesData?.allCategoryPolicy?.edges
    ?.flatMap(({node}: any) => node.policyTypeSet?.edges?.map(({node: type}: any) => type) || [])
    .find(({typePolicy, kind}: any) =>
      normalize(`${typePolicy || ''} ${kind || ''}`)
        .split(' ')
        .includes('aca')
    )

  const rowChecks = useMemo(
    () =>
      (parsed?.rows || []).map((row) => {
        const matches = users.filter((u) => nameMatches(row.agent, `${u.firstName} ${u.lastName}`))
        const npnValue = isLegacyRow(row) ? row.values[48] : row.agent
        const npnAgent = findAgentOfRecord(npnValue, users)
        const company = text(rowCompany(row))
        const carrier = findCarrier(company, carriers)
        const state = states.find(
          (item) =>
            normalize(item.name) === normalize(rowState(row)) ||
            normalize(item.code) === normalize(rowState(row))
        )
        const policyStatus = mapPolicyStatus(row.values[isLegacyRow(row) ? 41 : 36], statuses)
        return {
          row,
          user: matches.length === 1 ? matches[0] : null,
          npnAgent,
          carrier,
          state,
          policyStatus,
        }
      }),
    [parsed, users, carriers, states, statuses]
  )

  const unresolved = rowChecks.filter(
    ({user, carrier, state, policyStatus}) => !user || !carrier || !state || !policyStatus
  )
  const catalogsLoading =
    loadingUsers || loadingCarriers || loadingTypes || loadingStates || loadingStatuses
  const missingAcaType = !loadingTypes && !acaPolicyType

  const reset = () => {
    if (importing) return
    setParsed(null)
    setResult(null)
    setReplaceExisting(false)
    setProcessed(0)
    onClose()
  }

  const handleFile = async (file?: File) => {
    if (!file) return
    setReading(true)
    setParsed(null)
    setResult(null)
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), {cellDates: true})
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const matrix = XLSX.utils.sheet_to_json<Cell[]>(sheet, {header: 1, defval: null, raw: true})
      const headers = (matrix[0] || []).map(text)
      const format: ImportFormat =
        headers[0] === 'AGENT'
          ? 'legacy-export'
          : normalize(headers[0]) === normalize(BASE_HEADERS_ES[0])
          ? 'client-report-es'
          : 'client-report'
      const baseHeaders =
        format === 'legacy-export'
          ? LEGACY_BASE_HEADERS
          : format === 'client-report-es'
          ? BASE_HEADERS_ES
          : BASE_HEADERS
      const memberHeaders =
        format === 'legacy-export'
          ? LEGACY_MEMBER_HEADERS
          : format === 'client-report-es'
          ? MEMBER_HEADERS_ES
          : MEMBER_HEADERS
      const expected = [...baseHeaders]
      while (expected.length < headers.length) expected.push(...memberHeaders)
      const errors: string[] = []
      baseHeaders.forEach((header, index) => {
        if (headers[index] !== header)
          errors.push(
            intl.formatMessage(M.errColumnMismatch, {
              column: index + 1,
              expected: header,
              found: headers[index] || intl.formatMessage(M.errColumnEmpty),
            })
          )
      })
      for (let index = baseHeaders.length; index < headers.length; index++) {
        if (headers[index] !== expected[index])
          errors.push(
            intl.formatMessage(M.errMemberHeader, {column: index + 1, found: headers[index]})
          )
      }
      if ((headers.length - baseHeaders.length) % memberHeaders.length !== 0)
        errors.push(intl.formatMessage(M.errMemberBlocks))
      const rows = matrix
        .slice(1)
        .filter((row) => row.some((value) => text(value)))
        .map((values, index) =>
          format === 'legacy-export'
            ? {
                rowNumber: index + 2,
                reference: text(values[50]),
                agent: text(values[0]),
                holderName: text(values[4]),
                values,
                format,
              }
            : {
                rowNumber: index + 2,
                reference: text(values[0]),
                agent: text(values[2]),
                holderName: text(values[3]),
                values,
                format,
              }
        )
      rows.forEach((row) => {
        if (!row.reference)
          errors.push(intl.formatMessage(M.errRowNoReference, {row: row.rowNumber}))
        if (!row.agent) errors.push(intl.formatMessage(M.errRowNoAgent, {row: row.rowNumber}))
        const firstName = row.values[isLegacyRow(row) ? 5 : 4]
        const lastName = row.values[isLegacyRow(row) ? 7 : 6]
        if (!text(firstName) || !text(lastName))
          errors.push(intl.formatMessage(M.errRowNoHolder, {row: row.rowNumber}))
      })
      const referenceRows = new Map<string, number>()
      rows.forEach((row) => {
        const reference = normalize(row.reference)
        if (!reference) return
        const previousRow = referenceRows.get(reference)
        if (previousRow)
          errors.push(
            intl.formatMessage(M.errRowDuplicateReference, {
              row: row.rowNumber,
              previousRow,
            })
          )
        else referenceRows.set(reference, row.rowNumber)
      })
      setParsed({rows, errors: errors.slice(0, 30), fileName: file.name})
    } catch (error: any) {
      setParsed({
        rows: [],
        errors: [
          intl.formatMessage(
            {
              id: 'policies.list.import.error.fileUnreadable',
              defaultMessage: 'The file could not be read: {message}',
            },
            {message: error.message}
          ),
        ],
        fileName: file.name,
      })
    } finally {
      setReading(false)
    }
  }

  const importRow = async ({
    row,
    user,
    npnAgent,
    carrier,
    state,
    policyStatus,
  }: typeof rowChecks[number]) => {
    if (!user || !carrier || !state || !policyStatus)
      throw new Error(
        intl.formatMessage({
          id: 'policies.list.import.error.unresolvedRow',
          defaultMessage: 'Agent, Company, State or Status could not be resolved',
        })
      )
    const v = row.values
    const legacy = isLegacyRow(row)
    const baseLength = legacy ? LEGACY_BASE_HEADERS.length : BASE_HEADERS.length
    const memberBlockLength = legacy ? LEGACY_MEMBER_HEADERS.length : MEMBER_HEADERS.length
    const memberCount = Math.max(0, Math.floor((v.length - baseLength) / memberBlockLength))
    const members = Array.from({length: memberCount}, (_, index) => {
      const offset = baseLength + index * memberBlockLength
      const firstName = v[offset + 2]
      const middleName = legacy ? v[offset + 3] : null
      const lastName = v[offset + (legacy ? 4 : 3)]
      const secondLastName = legacy ? v[offset + 5] : null
      if (!text(firstName) && !text(lastName)) return null
      return {
        applicant: mapApplicant(v[offset]),
        relationship: mapRelationship(v[offset + 1]),
        firstName: text(firstName) || null,
        middleName: text(middleName) || null,
        lastName: text(lastName) || null,
        secondLastName: text(secondLastName) || null,
        dateBirth: toIsoDate(v[offset + (legacy ? 7 : 4)]),
        gender: mapGender(v[offset + (legacy ? 6 : 5)]),
        emailAddress: text(v[offset + (legacy ? 9 : 6)]) || null,
        phone: text(v[offset + (legacy ? 8 : 7)]) || null,
        annualIncome1: legacy ? String(number(v[offset + 10])) : undefined,
        maritalStatus: legacy ? text(v[offset + 11]) || null : undefined,
        ssn: text(v[offset + (legacy ? 12 : 9)]) || null,
        countryBirth: legacy ? text(v[offset + 13]) || null : undefined,
        immigrationStatus: mapImmigration(v[offset + (legacy ? 14 : 8)]),
        immigrationStatusCode: legacy ? text(v[offset + 15]) || null : undefined,
        rol: 'FAMILY',
      }
    }).filter(Boolean)
    const holderData = {
      applicant: legacy ? mapApplicant(v[3]) : true,
      rol: 'HOLDER',
      firstName: text(v[legacy ? 5 : 4]),
      middleName: text(v[legacy ? 6 : 5]),
      lastName: text(v[legacy ? 7 : 6]),
      secondLastName: legacy ? text(v[8]) : null,
      dateBirth: toIsoDate(v[legacy ? 10 : 7]),
      gender: mapGender(v[legacy ? 9 : 8]),
      emailAddress: text(v[legacy ? 12 : 9]),
      phone: text(v[legacy ? 11 : 10]),
      immigrationStatus: mapImmigration(v[legacy ? 17 : 11]),
      immigrationStatusCode: legacy ? text(v[18]) || null : null,
      ssn: text(v[legacy ? 15 : 12]),
      countryBirth: legacy ? text(v[16]) || null : null,
      greenCard: legacy ? null : text(v[13]),
      workPermitCard: legacy ? null : text(v[14]),
      maritalStatus: text(v[legacy ? 14 : 15]),
      employerName1: legacy ? null : text(v[22]),
      employerPhone1: legacy ? null : text(v[23]),
      positionOccupation1: legacy ? null : text(v[24]),
      annualIncome1: String(number(v[legacy ? 13 : 25])),
    }
    const policyData = {
      apizealCode: row.reference,
      policyName: row.holderName || `${text(v[4])} ${text(v[6])}`,
      applicants: Number(v[legacy ? 36 : 29]) || 1,
      members: members.length,
      dependents: members.filter((m: any) => !m.applicant).length,
      stateDocs: 'PENDING',
      paymentStatus: 'PENDING',
      effectiveDate: toIsoDate(v[legacy ? 40 : 30]),
      typePolicy: acaPolicyType?.id ? Number(parseId(acaPolicyType.id)) : null,
      carrier: Number(parseId(carrier.id)),
      user: Number(parseId(user.id)),
      createdBy: Number(parseId(user.id)),
      npnAgent: npnAgent?.id ? Number(parseId(npnAgent.id)) : null,
      commissionPayable: true,
      address1: text(v[legacy ? 19 : 16]),
      address2: text(v[legacy ? 20 : 17]),
      city: text(v[legacy ? 21 : 18]),
      stateCode: state.code,
      zipCode: text(v[legacy ? 23 : 20]),
      countyFips: '',
      mailingAddress1: legacy ? text(v[25]) : '',
      mailingAddress2: legacy ? text(v[26]) : '',
      mailingCity: legacy ? text(v[27]) : '',
      mailingZipCode: legacy ? text(v[29]) : '',
      planName: text(v[legacy ? 32 : 32]),
      metalLevel: inferMetalLevel(v[legacy ? 32 : 32], legacy ? null : v[33]),
      policyTotalCost: legacy ? number(v[37]) : number(v[34]) + number(v[35]),
      taxCredit: number(v[legacy ? 38 : 34]),
      premium: number(v[legacy ? 39 : 35]),
      status: policyStatus,
      canceledDate: legacy ? toIsoDate(v[45]) : null,
      marketplaceId: text(v[legacy ? 34 : 27]),
      memberId: text(v[legacy ? 35 : 26]),
      CMSPlanId: text(v[legacy ? 33 : 28]),
      NPN: legacy && !npnAgent ? text(v[48]) : '',
      FFM: legacy ? text(v[49]) : '',
    }

    const createResult = await client.mutate({
      mutation: CREATE_POLICY,
      variables: {policyData: {...policyData, holderData}},
    })
    const payload = createResult.data?.createPolicy
    if (!payload?.policy?.id)
      throw new Error(
        payload?.errors?.[0]?.message ||
          intl.formatMessage({
            id: 'policies.list.import.error.policyNotCreated',
            defaultMessage: 'The policy was not created',
          })
      )
    if (members.length) {
      const policyId = parseId(payload.policy.id)
      const memberResult = await client.mutate({
        mutation: UPDATE_POLICY_FULL,
        variables: {
          id: policyId,
          policyData: {createdBy: Number(parseId(user.id))},
          membersToCreate: members.map((member: any) => ({...member, policy: String(policyId)})),
        },
      })
      if (!memberResult.data?.updatePolicyFull?.success)
        throw new Error(
          memberResult.data?.updatePolicyFull?.errors?.[0]?.message ||
            intl.formatMessage({
              id: 'policies.list.import.error.membersFailed',
              defaultMessage: 'The policy was created, but its members failed',
            })
        )
    }
    return 'created'
  }

  const handleImport = async () => {
    if (!parsed || unresolved.length || parsed.errors.length) return
    setImporting(true)
    setProcessed(0)
    setResult(null)
    let created = 0
    let deleted = 0
    const errors: string[] = []
    if (replaceExisting) {
      try {
        const deleteResult = await client.mutate({
          mutation: DELETE_POLICIES_BY_REFERENCES,
          variables: {
            references: parsed.rows.map((row) => row.reference),
            confirm: true,
          },
        })
        const deletePayload = deleteResult.data?.deletePoliciesByReferences
        if (!deletePayload?.success)
          throw new Error(
            deletePayload?.errors?.[0]?.message ||
              intl.formatMessage({
                id: 'policies.list.import.error.replaceFailed',
                defaultMessage: 'The existing policies could not be replaced.',
              })
          )
        deleted = deletePayload.deletedCount || 0
      } catch (error: any) {
        setImporting(false)
        setResult({
          created: 0,
          deleted: 0,
          errors: [intl.formatMessage(M.errImportNotStarted, {message: error.message})],
        })
        return
      }
    }
    for (const checked of rowChecks) {
      try {
        await importRow(checked)
        created++
      } catch (error: any) {
        errors.push(
          intl.formatMessage(
            {
              id: 'policies.list.import.error.rowFailed',
              defaultMessage: 'Row {row} ({reference}): {message}',
            },
            {row: checked.row.rowNumber, reference: checked.row.reference, message: error.message}
          )
        )
      }
      setProcessed((value) => value + 1)
    }
    setImporting(false)
    setResult({created, deleted, errors})
    onImported()
  }

  return (
    <Modal show={isOpen} onHide={reset} centered size='lg' backdrop='static'>
      <Modal.Header closeButton={!importing}>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted'>{intl.formatMessage(M.intro)}</p>
        <Form.Group controlId='policies-import-file'>
          <Form.Label>{intl.formatMessage(M.fileLabel)}</Form.Label>
          <Form.Control
            type='file'
            accept='.xlsx,.xls'
            disabled={reading || importing}
            onChange={(e: any) => handleFile(e.target.files?.[0])}
          />
        </Form.Group>
        {reading && (
          <div className='text-center py-4'>
            <Spinner animation='border' size='sm' /> {intl.formatMessage(M.readingFile)}
          </div>
        )}
        {parsed && (
          <div className='mt-4'>
            <div className='d-flex gap-2 align-items-center mb-3'>
              <strong>{parsed.fileName}</strong>
              <Badge bg='primary' style={{color: '#fff', fontWeight: 600}}>
                {intl.formatMessage(M.policiesCount, {count: parsed.rows.length})}
              </Badge>
            </div>
            {!!parsed.errors.length && (
              <Alert variant='danger'>
                <strong>{intl.formatMessage(M.cannotImport)}</strong>
                <ul className='mb-0 mt-2'>
                  {parsed.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}
            {!parsed.errors.length && catalogsLoading && (
              <Alert variant='info'>{intl.formatMessage(M.validating)}</Alert>
            )}
            {!parsed.errors.length && missingAcaType && (
              <Alert variant='danger'>{intl.formatMessage(M.acaTypeMissing)}</Alert>
            )}
            {!parsed.errors.length && !catalogsLoading && unresolved.length > 0 && (
              <Alert variant='warning'>
                <strong>{intl.formatMessage(M.needCorrection, {count: unresolved.length})}</strong>
                <div className='table-responsive mt-2' style={{maxHeight: 220}}>
                  <Table size='sm'>
                    <thead>
                      <tr>
                        <th>{intl.formatMessage(M.colRow)}</th>
                        <th>{intl.formatMessage(M.colAgent)}</th>
                        <th>{intl.formatMessage(M.colCompany)}</th>
                        <th>{intl.formatMessage(M.colState)}</th>
                        <th>{intl.formatMessage(M.colProblem)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unresolved.slice(0, 20).map(({row, user, carrier, state, policyStatus}) => (
                        <tr key={row.rowNumber}>
                          <td>{row.rowNumber}</td>
                          <td>{row.agent}</td>
                          <td>{text(rowCompany(row))}</td>
                          <td>{text(rowState(row))}</td>
                          <td>
                            {!user
                              ? intl.formatMessage(M.agentNotFound)
                              : !carrier
                              ? intl.formatMessage(M.companyNotFound)
                              : !state
                              ? intl.formatMessage(M.stateNotFound)
                              : !policyStatus
                              ? intl.formatMessage(M.statusNotFound)
                              : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Alert>
            )}
            {!parsed.errors.length && !catalogsLoading && !missingAcaType && !unresolved.length && (
              <>
                <Alert variant='success'>{intl.formatMessage(M.ready)}</Alert>
                <Form.Check
                  id='replace-existing-policies'
                  type='checkbox'
                  checked={replaceExisting}
                  disabled={importing}
                  onChange={(event) => setReplaceExisting(event.target.checked)}
                  label={intl.formatMessage(M.replaceLabel)}
                />
                {replaceExisting && (
                  <Alert variant='danger' className='mt-2 mb-0 py-2'>
                    {intl.formatMessage(M.replaceWarning)}
                  </Alert>
                )}
              </>
            )}
          </div>
        )}
        {importing && (
          <div className='mt-3'>
            <ProgressBar
              now={parsed?.rows.length ? (processed / parsed.rows.length) * 100 : 0}
              label={`${processed}/${parsed?.rows.length || 0}`}
            />
          </div>
        )}
        {result && (
          <Alert className='mt-3' variant={result.errors.length ? 'warning' : 'success'}>
            <strong>{intl.formatMessage(M.createdCount, {count: result.created})}</strong>
            {result.deleted > 0 && (
              <div>{intl.formatMessage(M.replacedCount, {count: result.deleted})}</div>
            )}
            {result.errors.length > 0 && (
              <>
                <div>{intl.formatMessage(M.failedCount, {count: result.errors.length})}</div>
                <ul className='mb-0'>
                  {result.errors.slice(0, 20).map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={reset} disabled={importing}>
          {result ? (
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          ) : (
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          )}
        </Button>
        <Button
          onClick={handleImport}
          disabled={
            !parsed ||
            !!parsed.errors.length ||
            catalogsLoading ||
            missingAcaType ||
            !!unresolved.length ||
            importing ||
            !!result
          }
        >
          {importing ? (
            <>
              <Spinner animation='border' size='sm' /> {intl.formatMessage(M.importing)}
            </>
          ) : (
            <>
              <i className='bi bi-upload' />{' '}
              {replaceExisting
                ? intl.formatMessage(M.btnReplaceImport)
                : intl.formatMessage(M.btnImport)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
